import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  includeFAQ?: boolean;
}

const SEO = ({
  title = "Marina González Hurtado | Nutricionista Clínica y Deportiva en Madrid",
  description = "Mejora tu salud con Marina González. Planes de alimentación personalizados para nutrición clínica y deportiva. Agenda tu cita en Madrid u Online.",
  canonical = "https://mghnutricion.es",
  ogImage = "https://mghnutricion.es/og-image.png",
  includeFAQ = false,
}: SEOProps) => {
  const schemaLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": canonical,
    name: "Marina González Hurtado - Nutricionista",
    description: description,
    image: ogImage,
    url: canonical,
    telephone: "+34614515974",
    email: "marinaglez@mghnutricion.es",
    priceRange: "€€",
    address: [
      {
        "@type": "PostalAddress",
        streetAddress: "Plaza Miguel Echegaray 5",
        addressLocality: "Humanes de Madrid",
        postalCode: "28970",
        addressCountry: "ES",
      },
      {
        "@type": "PostalAddress",
        streetAddress: "Calle Pelayos 3",
        addressLocality: "Fuenlabrada",
        postalCode: "28945",
        addressCountry: "ES",
      },
    ],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "20:00",
    },
  };

  const schemaPerson = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Marina González Hurtado",
    jobTitle: "Nutricionista",
    description: "Nutricionista y farmacéutica colegiada especializada en nutrición clínica y deportiva.",
    url: canonical,
    email: "marinaglez@mghnutricion.es",
    telephone: "+34614515974",
    image: ogImage,
    sameAs: [],
    alumniOf: [
      {
        "@type": "EducationalOrganization",
        name: "Universidad Alfonso X el Sabio",
      },
      {
        "@type": "EducationalOrganization",
        name: "Universidad Francisco de Vitoria",
      },
    ],
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Colegiada",
      recognizedBy: {
        "@type": "Organization",
        name: "CODINMA",
      },
      identifier: "MAD01587",
    },
    knowsAbout: ["Nutrición Clínica", "Nutrición Deportiva", "Dietética", "Biotecnología", "Farmacia"],
  };

  const schemaFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "¿Cuánto dura una consulta de nutrición?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "La primera consulta tiene una duración aproximada de 60 minutos, ya que incluye una evaluación completa de tu situación. Las consultas de seguimiento duran entre 45 y 60 minutos.",
        },
      },
      {
        "@type": "Question",
        name: "¿Con qué frecuencia debo tener consultas de seguimiento?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Normalmente recomiendo consultas cada 2-4 semanas, dependiendo de tus objetivos y necesidades específicas.",
        },
      },
      {
        "@type": "Question",
        name: "¿Las consultas online son igual de efectivas que las presenciales?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Sí, las consultas online incluyen exactamente los mismos servicios que las presenciales. Realizamos la sesión por videollamada y tienes acceso al mismo seguimiento personalizado, plan nutricional y soporte continuo.",
        },
      },
      {
        "@type": "Question",
        name: "¿Voy a pasar hambre con el plan nutricional?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. Mis planes nutricionales están diseñados para ser equilibrados y satisfactorios. No trabajo con dietas restrictivas ni conteo obsesivo de calorías.",
        },
      },
      {
        "@type": "Question",
        name: "¿En cuánto tiempo veré resultados?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Las primeras mejoras suelen notarse entre las 3 y 4 primeras semanas: más energía, mejor digestión, mejor descanso. Los cambios físicos son progresivos y sostenibles a largo plazo.",
        },
      },
      {
        "@type": "Question",
        name: "¿El plan nutricional se adapta a mis gustos y estilo de vida?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutamente. El plan está 100% personalizado según tus preferencias alimentarias, horarios, ritmo de vida y objetivos.",
        },
      },
      {
        "@type": "Question",
        name: "¿Qué incluye la primera consulta?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "La primera consulta incluye una evaluación completa de tu historial de salud, hábitos alimentarios actuales, análisis de composición corporal, definición de objetivos realistas y la creación de tu plan nutricional inicial personalizado.",
        },
      },
      {
        "@type": "Question",
        name: "¿Merece la pena el bono de 3 sesiones?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "El bono de 3 sesiones de seguimiento supone un ahorro de 15€ respecto a contratar las sesiones por separado. Es ideal si buscas un seguimiento continuo para lograr resultados duraderos.",
        },
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="author" content="Marina González Hurtado" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content="Marina González Nutrición" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#ED5879" />
      <meta name="geo.region" content="ES-M" />
      <meta name="geo.placename" content="Madrid" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(schemaLocalBusiness)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaPerson)}</script>
      {includeFAQ && <script type="application/ld+json">{JSON.stringify(schemaFAQ)}</script>}
    </Helmet>
  );
};

export default SEO;
